import React, { FC, useState, useRef, useEffect } from 'react';
import injectSheet from 'react-jss';
import CogIcon from 'Assets/ui/cog.svg';
import Tooltip from 'HOC/Tooltip';
import arrayMove from 'Utils/arrayMove';
import SimpleBar from 'simplebar-react';
import compareArrays from 'Utils/compareArrays';
import styleSheet from './styles';
import CheckBox from '../CheckBox';
import ActionButtons from './components/ActionButtons';
import CheckedList from './components/CheckedList';
import RevertToDefault from './components/RevertToDefault';

interface Props {
  allColumns: any[string];
  tableColumns: any[string];
  defaultColumns: any[string];
  sortingColumn: string;
  sortingDirection: string;
  columnConfig: any;
  changeCols?: any;
  classes: any;
  exportSettings: any;
  setExportSettings: (val: any) => void;
}

const ExportAudienceList: FC<Props> = (props: Props) => {
  const {
    allColumns,
    tableColumns,
    defaultColumns,
    sortingColumn,
    sortingDirection,
    columnConfig,
    changeCols,
    classes,
    exportSettings,
    setExportSettings,
  } = props;

  const [tableCols, setTableCols] = useState(tableColumns);
  const [sortColumn, setSortColumn] = useState(sortingColumn);
  const [sortDirection, setSortDirection] = useState(sortingDirection);
  const [revertEnable, setRevertEnable] = useState(
    !compareArrays(defaultColumns, tableCols) ||
      !(sortingColumn === 'dateRegistered' && sortingDirection === 'desc')
  );
  const [applyEnable, setApplyEnable] = useState(
    !compareArrays(tableColumns, tableCols)
  );

  useEffect(() => {
    setRevertEnable(
      !compareArrays(defaultColumns, tableCols) ||
        !(sortColumn === 'dateRegistered' && sortDirection === 'desc')
    );
    setApplyEnable(!compareArrays(tableColumns, tableCols));
  }, [JSON.stringify(tableCols)]);

  useEffect(() => {
    setTableCols(tableColumns);
    setApplyEnable(!compareArrays(tableColumns, tableCols));
    setSortColumn(sortingColumn);
    setSortDirection(sortingDirection);
    setRevertEnable(
      !compareArrays(defaultColumns, tableCols) ||
        !(sortingColumn === 'dateRegistered' && sortingDirection === 'desc')
    );
  }, [sortingColumn, sortingDirection, JSON.stringify(tableColumns)]);

  const cogRef = useRef(null);
  const cogFocus = () => {
    cogRef.current.focus();
  };
  const onCogClick = (event: any) => {
    event.stopPropagation();
    setExportSettings(!exportSettings);
    if (!exportSettings) setTableCols(tableColumns);
  };

  const onSortEnd = ({ oldIndex, newIndex }: any) => {
    setTableCols(arrayMove(tableCols, oldIndex, newIndex));
  };

  const onCheckBoxClick = (event: {
    target: { value: string; id: string };
  }) => {
    if (event.target.value) {
      setTableCols([...tableCols, event.target.id]);
    } else {
      if (tableCols.length === 1) return;
      setTableCols(
        tableCols.filter((column: any) => column !== event.target.id)
      );
    }
  };

  const onRevert = () => {
    if (revertEnable) {
      setTableCols(defaultColumns);
      setSortColumn('dateRegistered');
      setSortDirection('desc');
      setRevertEnable(false);
      setApplyEnable(true);
    }
  };

  return (
    <>
      <div className={classes.settingsContainer}>
        <button
          aria-label="Inquiries Configuration"
          ref={cogRef}
          className={classes.iconContainer}
          onClick={onCogClick}
          type="button"
        >
          <Tooltip
            position="top"
            text="Column Configuration"
            offsetTop={-45}
            offsetLeft={-70}
          >
            <CogIcon />
          </Tooltip>
        </button>
        {exportSettings && (
          <div className={classes.exportSettings}>
            <SimpleBar
              style={{
                height: '532px',
                width: '255px',
                borderRadius: '8px',
                backgroundColor: '#FFFFFF',
                boxShadow: '0 4px 8px 0 rgba(0,0,0,0.2)',
                display: 'inline-block',
                top: '30px',
                right: '7px',
                zIndex: 2,
                position: 'absolute',
              }}
            >
              <CheckedList
                onSortEnd={onSortEnd}
                axis="y"
                lockAxis="y"
                lockToContainerEdges
                lockOffset={tableCols.length === 1 ? '0px' : '100%'}
                distance={1}
                tableCols={tableCols}
                columnConfig={columnConfig}
                onCheckBoxClick={onCheckBoxClick}
              />
              <div className={classes.divider} />

              <div className={classes.uncheckedColumns}>
                {allColumns
                  .filter(
                    (col: any) => !tableCols.includes(col) && columnConfig[col]
                  )
                  .map((column: string) => (
                    <div className="column" key={column}>
                      <CheckBox
                        overrideStyles={{
                          display: 'inline-block',
                          verticalAlign: 'middle',
                          whiteBoxDisplay: 'inline-block',
                        }}
                        onClick={onCheckBoxClick}
                        id={column}
                        isChecked={false}
                      >
                        <span className="columnName">
                          {columnConfig[column].title}
                        </span>
                      </CheckBox>
                    </div>
                  ))}
              </div>
              <span
                style={{
                  background: 'rgba(255, 255, 255, 1)',
                  display: 'block',
                  position: 'sticky',
                  paddingRight: '20px',
                  bottom: '0%',
                }}
              >
                <RevertToDefault
                  revertEnable={revertEnable}
                  onClick={onRevert}
                />
                <ActionButtons
                  changeCols={changeCols}
                  setExportSettings={setExportSettings}
                  tableCols={tableCols}
                  setTableCols={setTableCols}
                  originalCols={tableColumns}
                  applyEnable={applyEnable}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  cogFocus={cogFocus}
                />
              </span>
            </SimpleBar>
          </div>
        )}
      </div>
    </>
  );
};

ExportAudienceList.defaultProps = {
  changeCols: undefined,
};

export default injectSheet(styleSheet)(ExportAudienceList);
